"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import { StyledFloatingButton, StyledWidgetNav } from "./widgetNavbar.styles";
import { IPlatform, IPlugin } from "../../types";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useInView } from "react-intersection-observer";
import { CustomLink } from "../link/link.comp";
import { trackEvent } from "@/lib/track";
import { createMobileAwareClickHandler } from "../../helpers/mobile-redirect";

interface IWidgetNavbarProps {
  widget: IPlugin;
  platform?: IPlatform;
  setFormPopupOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  showLogo?: boolean;
  // Whether to render the "Templates" tab. Computed on the server (see
  // src/helpers/templatesTab.ts) so the tab is correct in the initial HTML.
  // Hidden when the widget has no templates or its /templates path redirects
  // back to the hub. Left undefined it defaults to shown, preserving the
  // original behavior for any caller that does not pass it.
  showTemplatesTab?: boolean;
}

export const WidgetNavbar = ({
  widget,
  platform,
  setFormPopupOpen,
  showLogo = false,
  showTemplatesTab,
}: IWidgetNavbarProps) => {
  const pathname = usePathname();
  const [loaded, setLoaded] = useState(false);
  const { ref, inView } = useInView({
    threshold: 0,
  });
  const [isBrackets, setIsBrackets] = useState<boolean>(false);
  const [shake, setShake] = useState(false);
  // Pages without a playground behave as if it was already passed.
  const [playgroundPassed, setPlaygroundPassed] = useState(true);

  const widgetSlug = widget.seoSlug || widget.slug;
  const overviewPath = platform?.title
    ? `/widgets/${widgetSlug}/${platform.slug}`
    : `/widgets/${widgetSlug}`;
  const templatesPath = platform?.title
    ? `/widgets/${widgetSlug}/${platform.slug}/templates`
    : `/widgets/${widgetSlug}/templates`;
  const howToAddPath = platform?.title
    ? `/widgets/${widgetSlug}/${platform.slug}/how-to-add`
    : `/widgets/${widgetSlug}/how-to-add`;

  useEffect(() => {
    setLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pages testing the embedded editor playground (e.g. /widgets/countdown)
  // mark it with data-cn-live-playground. On those pages the floating CTA
  // never shows until the visitor has scrolled completely PAST the playground
  // (its bottom above the viewport) — not before reaching it, and not while
  // any part of it is on screen.
  useEffect(() => {
    const playground = document.querySelector("[data-cn-live-playground]");
    if (!playground) return;
    setPlaygroundPassed(false);
    const observer = new IntersectionObserver(
      ([entry]) =>
        setPlaygroundPassed(
          !entry.isIntersecting && entry.boundingClientRect.bottom < 0
        ),
      { threshold: 0 }
    );
    observer.observe(playground);
    return () => observer.disconnect();
  }, []);

  function renderNavContent(isFixed?: boolean) {
    return (
      <div className="links">
        {(isFixed || showLogo) && (
          <Link href={overviewPath} className="logo">
            <Image
              src={widget.logo}
              alt={`${widget.name} ${
                platform?.title ? `for ${platform.title}` : ""
              } logo`}
              width={30}
              height={30}
            />
            {widget.name}
          </Link>
        )}
        <ul>
          <li>
            <Link
              className={
                pathname === `/widgets/${widgetSlug}` ||
                pathname === `/widgets/${widgetSlug}/${platform?.slug}`
                  ? "active"
                  : ""
              }
              href={overviewPath}
            >
              Overview
            </Link>
          </li>
          {showTemplatesTab !== false && (
            <li className="templates">
              <Link
                className={
                  pathname === `/widgets/${widgetSlug}/templates` ||
                  pathname ===
                    `/widgets/${widgetSlug}/${platform?.slug}/templates`
                    ? "active"
                    : ""
                }
                href={templatesPath}
              >
                Templates
              </Link>
            </li>
          )}
          <li className="how-to-add">
            <Link
              className={
                pathname ===
                  `/widgets/${widgetSlug}/${platform?.slug}/how-to-add` ||
                pathname === `/widgets/${widgetSlug}/how-to-add`
                  ? "active"
                  : ""
              }
              href={howToAddPath}
            >
              How to add
            </Link>
          </li>
          {!inView && (
            <>
              {widget.status !== "coming-soon" ? (
                <>
                  <li>
                    <Link
                      className={
                        pathname === `/widgets/${widgetSlug}/pricing` ||
                        pathname ===
                          `/widgets/${widgetSlug}/${platform?.slug}/pricing`
                          ? "active"
                          : ""
                      }
                      href={
                        isBrackets
                          ? `https://www.commoninja.com/embed/website/pricing/bracketsninja?via=commonninja`
                          : `/pricing`
                      }
                      target={isBrackets ? "_blank" : ""}
                    >
                      Pricing
                    </Link>
                  </li>
                  <li className="cta-li">
                    <CustomLink
                      external
                      title={`Free ${widget.name} ${
                        platform?.title ? `for ${platform.title}` : ""
                      }`}
                      href={`/${widget.slug}/editor`}
                      onClick={createMobileAwareClickHandler(() => {
                        // trackEvent(`Try for free ${widget.name}`, { serviceName: widget.serviceName });
                      }, `/${widget.slug}/editor`)}
                      id="menu-try-cta"
                    >
                      Try for free
                    </CustomLink>
                  </li>
                </>
              ) : (
                <li className="cta-li">
                  <CustomLink
                    external
                    title={`Get Notified when ${widget.name} get released.`}
                    className="try-button"
                    onClick={() => {
                      // trackEvent(`Coming Soon Landing page ${widget.name}`);
                      if (setFormPopupOpen) {
                        setFormPopupOpen(true);
                      }
                    }}
                  >
                    Get Notified
                  </CustomLink>
                </li>
              )}
            </>
          )}
        </ul>
      </div>
    );
  }

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      window.location.pathname.includes("brackets")
    ) {
      setIsBrackets(true);
    } else {
      setIsBrackets(false);
    }

    // Toggle shake animation once every 5 seconds for 2 seconds
    const interval = setInterval(() => {
      setShake(true);
      setTimeout(() => {
        setShake(false);
      }, 1000);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <StyledWidgetNav
        ref={ref}
        className={`${inView || !loaded ? "show" : ""}`}
      >
        {renderNavContent()}
      </StyledWidgetNav>
      <StyledWidgetNav className={`fixed ${!inView && loaded ? "show" : ""}`}>
        {renderNavContent(true)}
      </StyledWidgetNav>
      {widget.status !== "coming-soon" && (
        <StyledFloatingButton
          className={`floating-button ${shake ? "shake" : ""}`}
          style={{
            display: !inView && loaded && playgroundPassed ? "block" : "none",
          }}
          href={`/${widget.slug}/editor`}
          onClick={createMobileAwareClickHandler(() => {
            trackEvent(`Floating try for free`, {
              serviceName: widget.serviceName,
            });
          }, `/${widget.slug}/editor`)}
          id="floating-try-cta"
        >
          Try for free!
        </StyledFloatingButton>
      )}
    </>
  );
};
